import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { Observable } from 'rxjs';
import { AuditEvent } from '@core/data/notifications/notifications.model';
import { NotificationsService } from '@core/data/notifications/notifications.service';
import { select, Store } from '@ngrx/store';
import { messengerNotificationsSelector } from '@core/store/selectors/notifications.selectors';
import { map, take } from 'rxjs/operators';
import { MessengerService } from '@core/services/messenger/messenger.service';
import {
  MarkAsReadNotification,
  UpdateNotifications,
} from '@core/store/actions/notifications.actions';
import { userRoleNewSelector } from '@core/store/selectors/user-role.selectors';
import { USER_ROLE } from '@core/store/reducers/session-info.reducer';
import { CommonModule } from '@angular/common';
import { TranslocoModule } from '@jsverse/transloco';
import { CypressDirectiveModule } from '@shared/directives/cypress/cypress-directive.module';
import { MatIconModule } from '@angular/material/icon';
import { SpinnerComponent } from '@xt/ui-kit';
import { ButtonModule } from '@shared/components/atoms/button/button.module';
import { Router } from '@angular/router';
import { PatientRepresentationService } from '@xt/patient-representations-sdk';
import { TabNavigationService } from '@core/services/tab-navigation.service';

const NOTIFICATION_TYPE_CODES = {
  DOCUMENTS: '200',
  APPOINTMENTS: '300',
  DIARY: '400',
  PRIVACY_COCKPIT: '600',
  QUESTIONNAIRES: '800',
  LOGBOOK: '900',
} as const;

interface NotificationRoute {
  hcp?: string;
  representative?: (representationId: string) => { url: string; tab: number };
  patient?: { url: string; tab: number };
}

type NavigationTarget = { url: string; tab: number } | undefined;

const NOTIFICATION_ROUTES: Record<string, NotificationRoute> = {
  [NOTIFICATION_TYPE_CODES.DOCUMENTS]: {
    hcp: 'documents',
    representative: (representationId) => ({
      url: `dashboard/representations/${representationId}/documents`,
      tab: 0,
    }),
    patient: { url: 'dashboard/documents', tab: 0 },
  },
  [NOTIFICATION_TYPE_CODES.APPOINTMENTS]: {
    representative: (representationId) => ({
      url: `dashboard/representations/${representationId}/appointments`,
      tab: 0,
    }),
    patient: { url: 'dashboard/appointments', tab: 0 },
  },
  [NOTIFICATION_TYPE_CODES.DIARY]: {
    hcp: 'diary',
    representative: (representationId) => ({
      url: `dashboard/representations/${representationId}/diary`,
      tab: 0,
    }),
  },
  [NOTIFICATION_TYPE_CODES.PRIVACY_COCKPIT]: {
    representative: (representationId) => ({
      url: `dashboard/representations/${representationId}/privacy-cockpit`,
      tab: 2,
    }),
    patient: { url: 'dashboard/privacy-cockpit', tab: 2 },
  },
  [NOTIFICATION_TYPE_CODES.QUESTIONNAIRES]: {
    hcp: 'questionnaires',
    representative: (representationId) => ({
      url: `dashboard/representations/${representationId}/documents`,
      tab: 2,
    }),
    patient: { url: 'dashboard/questionnaires', tab: 2 },
  },
  [NOTIFICATION_TYPE_CODES.LOGBOOK]: {
    hcp: 'logbook',
    representative: (representationId) => ({
      url: `dashboard/representations/${representationId}/logbook`,
      tab: 2,
    }),
  },
};

@Component({
  selector: 'xt-portal-notification-menu',
  templateUrl: './notification-menu.component.html',
  styleUrls: ['./notification-menu.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    CypressDirectiveModule,
    MatIconModule,
    ButtonModule,
    SpinnerComponent,
  ],
})
export class NotificationMenuComponent implements OnInit {
  public notifications$: Observable<AuditEvent[]>;
  public unreadNotificationCount$: Observable<number>;
  public hasUnreadNotifications$: Observable<boolean>;
  public readonly loading$ = this.notificationsService.loading$;
  role: string[] = [];

  constructor(
    public dialogRef: MatDialogRef<NotificationMenuComponent>,
    private notificationsService: NotificationsService,
    private messengerService: MessengerService,
    private store: Store,
    private router: Router,
    private patientRepresentationService: PatientRepresentationService,
    private tabNavigationService: TabNavigationService,
    @Inject(MAT_DIALOG_DATA) public data: [],
  ) {}

  ngOnInit(): void {
    this.notifications$ = this.store.pipe(
      select(messengerNotificationsSelector),
      map((events) =>
        events.sort(
          (a, b) => new Date(b.time).getTime() - new Date(a.time).getTime(),
        ),
      ),
    );
    this.store.pipe(select(userRoleNewSelector)).subscribe((role) => {
      this.role.push(...role);
    });

    this.notificationsService.fetchDataBasedOnContext();

    this.unreadNotificationCount$ =
      this.notificationsService.unreadNotificationCount$;
    this.hasUnreadNotifications$ =
      this.notificationsService.hasUnreadNotifications$;
  }

  /**
   * marks all notifications as read
   *
   */
  public markAllAsRead(): void {
    const messengerUnreadNotifications = [];

    this.notifications$.pipe(take(1)).subscribe((notifications) => {
      const unreadNotifications = notifications.filter(
        (notification) => !notification.read,
      );
      for (const notification of unreadNotifications) {
        if (notification.path) {
          messengerUnreadNotifications.push(notification);
        } else {
          this.markNotificationAsRead(notification);
        }
      }

      if (messengerUnreadNotifications.length) {
        this.markAsReadMessengerNotifications(messengerUnreadNotifications);
      }
    });
  }

  /**
   * marks notification with given id or messenger notification as read
   *
   * @param notification
   * @param event event to stop propagation to avoid redirect to room
   */
  public markAsRead(
    notification: AuditEvent,
    event?: { stopPropagation: () => void },
  ): void {
    if (event) event.stopPropagation();

    if (notification.path) {
      this.handleMessengerNotification(notification);
    } else {
      this.markNotificationAsRead(notification);
      this.navigateToNotificationTarget(notification);
    }
  }

  private markAsReadMessengerNotifications(notifications: AuditEvent[]): void {
    this.store.dispatch(MarkAsReadNotification({ isMarkAsRead: true }));

    notifications.forEach((notification) =>
      this.store.dispatch(UpdateNotifications({ notification })),
    );
    this.messengerService.markAsRead(notifications);

    // Fix for getting unread notifications from messenger
    setTimeout(() => {
      this.store.dispatch(MarkAsReadNotification({ isMarkAsRead: false }));
    }, 10000);
  }

  private handleMessengerNotification(notification: AuditEvent): void {
    // Mark as read messenger notification
    this.markAsReadMessengerNotifications([notification]);

    this.router.navigateByUrl('/dashboard/messages#' + notification.path);
    this.dialogRef.close();

    setTimeout(() => {
      // Notify messenger about hash change
      const changeEvt = new Event('hashchange');
      window.dispatchEvent(changeEvt);
    }, 0);
  }

  /**
   * Marks a notification as read without navigating
   *
   * @param notification
   */
  private markNotificationAsRead(notification: AuditEvent): void {
    this.store.dispatch(UpdateNotifications({ notification }));
    this.notificationsService.markAsReadById(notification.id);
  }

  private navigateToNotificationTarget(notification: AuditEvent): void {
    const notificationTypeCode = notification._resource?.subtype[0]?.code;

    if (this.role.includes(USER_ROLE.HCP)) {
      this.handleHcpNotification(notification, notificationTypeCode);
    } else if (this.role.includes(USER_ROLE.VERIFIED_REPRESENTATIVE)) {
      this.handleRepresentativeNotification(notification, notificationTypeCode);
    } else {
      this.handlePatientNotification(notificationTypeCode);
    }
  }

  private handleHcpNotification(
    notification: AuditEvent,
    notificationTypeCode: string,
  ): void {
    if (
      Object.values(NOTIFICATION_TYPE_CODES).includes(
        notificationTypeCode as any,
      )
    ) {
      const patientId = notification._resource.agent[0].who.identifier.value;
      const target = this.getHcpTargetForNotificationType(notificationTypeCode);

      if (notificationTypeCode === NOTIFICATION_TYPE_CODES.APPOINTMENTS) {
        this.navigateToAppointments();
      } else if (target) {
        this.navigateToPatientDetail(patientId, target);
      }
    }
  }

  private handleRepresentativeNotification(
    notification: AuditEvent,
    notificationTypeCode: string,
  ): void {
    const patientId = notification._resource.agent[0].who.identifier.value;
    this.getRepresentationIdFromPatientId$(patientId)
      .pipe(take(1))
      .subscribe((representationId) => {
        const target = this.getRepresentativeTargetForNotificationType(
          notificationTypeCode,
          representationId,
        );

        if (target) {
          this.navigateWithTab(target.url, target.tab);
        } else {
          // Fallback for unknown notification codes
          this.navigateWithTab(
            `dashboard/representations/${representationId}`,
            0,
          );
        }
      });
  }

  private getRepresentationIdFromPatientId$(
    patientId: string,
  ): Observable<string> {
    return this.patientRepresentationService
      .fetchData$({
        patient: `Patient/${patientId}`,
      })
      .pipe(
        take(1),
        map((patientRepresentations) => {
          return patientRepresentations.find((relatedPerson) => {
            return relatedPerson.patientId === patientId;
          }).id;
        }),
      );
  }

  private handlePatientNotification(notificationTypeCode: string): void {
    const target = this.getPatientNavigationTarget(notificationTypeCode);

    if (notificationTypeCode === NOTIFICATION_TYPE_CODES.APPOINTMENTS) {
      this.navigateToAppointments();
    } else if (target) {
      this.navigateWithTab(target.url, target.tab);
    }
  }

  private getHcpTargetForNotificationType(
    notificationTypeCode: string,
  ): string | undefined {
    return NOTIFICATION_ROUTES[notificationTypeCode]?.hcp;
  }

  private getRepresentativeTargetForNotificationType(
    notificationTypeCode: string,
    representationId: string,
  ): NavigationTarget {
    const route = NOTIFICATION_ROUTES[notificationTypeCode]?.representative;
    return route ? route(representationId) : undefined;
  }

  private getPatientNavigationTarget(
    notificationTypeCode: string,
  ): NavigationTarget {
    return NOTIFICATION_ROUTES[notificationTypeCode]?.patient;
  }

  private navigateWithTab(url: string, tab: number): void {
    const currentUrl = this.router.url.split('?')[0].replace(/^\//, '');
    if (currentUrl === url) {
      this.tabNavigationService.emitTab(tab);
      this.dialogRef.close();
      return;
    } else {
      this.router.navigate([url], { state: { tab } });
    }
    this.dialogRef.close();
  }

  private navigateToPatientDetail(patientId: string, target: string): void {
    const url = `/dashboard/patients/${patientId}/${target}`;
    this.router.navigate([url]);
    this.dialogRef.close();
  }

  private navigateToAppointments(): void {
    this.router.navigate(['/dashboard/appointments']);
    this.dialogRef.close();
  }
}
